import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Download, Lock, X } from 'lucide-react'
import { useEditor } from '../store/editor'
import { ApiError, cancelExport, loginUrl, startExport, type ExportProgress } from '../lib/api'
import { maxShortSide, plansUrl } from '../lib/plan'
import { track } from '../lib/analytics'
import { buildExportRequest, outputSize, type ExportOptions } from '../lib/exportMap'
import { formatShort } from '../lib/model'
import { saveNow } from '../lib/persist'

const RES = [
  { v: 720, label: '720p' },
  { v: 1080, label: '1080p' },
  { v: 1440, label: '2K' },
  { v: 2160, label: '4K' },
]

type Phase = 'setup' | 'running' | 'done' | 'error'

export default function ExportDialog() {
  const project = useEditor((s) => s.project)
  const user = useEditor((s) => s.user)
  const close = () => useEditor.getState().set({ dialog: null })
  // Default to the resolution the Vaideo video was created at (no upscaling),
  // else the canvas' own short side, else 1080p.
  const origShort = project.origin ? Math.min(project.origin.width, project.origin.height) : 0
  const canvasShort = Math.min(project.settings.width, project.settings.height)
  const maxShort = maxShortSide(user)
  const defShort0 = RES.some((r) => r.v === origShort) ? origShort : RES.some((r) => r.v === canvasShort) ? canvasShort : 1080
  const defShort = Math.min(defShort0, maxShort)
  const [opts, setOpts] = useState<ExportOptions>({
    shortSide: defShort, fps: project.settings.fps || 30, quality: 'high', filename: project.name,
  })
  const [phase, setPhase] = useState<Phase>('setup')
  const [prog, setProg] = useState<ExportProgress | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // 402 (plan) / 429 (too many exports): shown as a plan notice, not as a failure.
  const [limitMsg, setLimitMsg] = useState<{ status: number; text: string } | null>(null)
  const [dl, setDl] = useState<string | null>(null)
  const job = useRef<string | null>(null)
  const es = useRef<EventSource | null>(null)
  const t0 = useRef(0)

  useEffect(() => () => es.current?.close(), [])

  const req = buildExportRequest(project, opts)
  const size = outputSize(project, opts.shortSide)
  const estMB = ((req.videoBitrateK + req.audioBitrateK) * project.durationSec) / 8 / 1024

  const start = async () => {
    setErr(null)
    setLimitMsg(null)
    setPhase('running')
    setProg(null)
    t0.current = Date.now()
    saveNow({ silent: true })
    try {
      const r = await startExport(req)
      job.current = r.jobId
      setDl(r.downloadUrl)
      const src = new EventSource(r.progressUrl, { withCredentials: true })
      es.current = src
      src.addEventListener('progress', (ev) => {
        const p = JSON.parse((ev as MessageEvent).data) as ExportProgress
        setProg(p)
        if (p.done) {
          src.close()
          if (p.error) {
            setErr(p.error === 'cancelled' ? 'Exportación cancelada' : p.error)
            setPhase('error')
          } else {
            setPhase('done')
            if (p.downloadUrl) setDl(p.downloadUrl)
            track('Editor Export', { resolution: RES.find((x) => x.v === opts.shortSide)?.label ?? `${opts.shortSide}p` })
          }
        }
      })
      src.onerror = () => {
        // SSE dropped (proxy timeout): keep polling by reconnecting automatically;
        // EventSource does that on its own unless the job is gone.
      }
    } catch (e) {
      if (e instanceof ApiError && (e.status === 402 || e.status === 429)) {
        setLimitMsg({ status: e.status, text: e.message })
        setPhase('setup')
      } else {
        setErr((e as Error).message)
        setPhase('error')
      }
    }
  }

  const cancel = async () => {
    if (job.current) await cancelExport(job.current)
    es.current?.close()
    setPhase('setup')
  }

  const pct = Math.round((prog?.progress ?? 0) * 100)
  const elapsed = (Date.now() - t0.current) / 1000
  const eta = prog && prog.progress > 0.03 ? elapsed * (1 / prog.progress - 1) : null

  return (
    <div className="overlay" onPointerDown={(e) => { if (e.target === e.currentTarget && phase !== 'running') close() }}>
      <div className="dialog">
        <h3>
          Exportar vídeo
          {phase !== 'running' && <button className="btn ghost icon sm" onClick={close}><X size={16} /></button>}
        </h3>
        {!user ? (
          <>
            <p>Inicia sesión en Vaideo (gratis) para exportar tu vídeo en MP4.</p>
            <p className="hint">Tu proyecto queda guardado en este navegador mientras tanto.</p>
            <div className="actions"><a className="btn primary" href={loginUrl()}>Iniciar sesión</a></div>
          </>
        ) : phase === 'setup' || phase === 'error' ? (
          <>
            <div className="group" style={{ background: 'transparent' }}>
              <div className="row"><label>Nombre</label>
                <input className="txt" value={opts.filename} onChange={(e) => setOpts({ ...opts, filename: e.target.value })} />
              </div>
              <div className="row"><label>Resolución</label>
                <div className="seg">
                  {RES.map((r) => {
                    const locked = r.v > maxShort
                    return (
                      <button key={r.v} className={`${opts.shortSide === r.v ? 'on' : ''}${locked ? ' locked' : ''}`}
                        disabled={locked}
                        title={locked ? 'Disponible en Pro' : r.v === origShort ? 'Resolución original del vídeo' : undefined}
                        onClick={() => setOpts({ ...opts, shortSide: r.v })}>
                        {locked && <Lock size={11} />}{r.label}{r.v === origShort ? ' ★' : ''}
                      </button>
                    )
                  })}
                </div>
              </div>
              {maxShort < 2160 && (
                <div className="hint plan-upsell">
                  <Lock size={12} /> 2K y 4K: <a href={plansUrl('editor-4k')} target="_blank" rel="noopener">Disponible en Pro</a>
                </div>
              )}
              <div className="row"><label>Fotogramas</label>
                <div className="seg">
                  {[24, 25, 30, 60].map((f) => (
                    <button key={f} className={opts.fps === f ? 'on' : ''} onClick={() => setOpts({ ...opts, fps: f })}>{f} fps</button>
                  ))}
                </div>
              </div>
              <div className="row"><label>Calidad</label>
                <div className="seg">
                  {([['normal', 'Normal'], ['high', 'Alta'], ['max', 'Máxima']] as const).map(([k, l]) => (
                    <button key={k} className={opts.quality === k ? 'on' : ''} onClick={() => setOpts({ ...opts, quality: k })}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="hint">
                MP4 (H.264 + AAC) · {size.width}×{size.height} · {formatShort(project.durationSec)} · ≈ {estMB < 1 ? '<1' : Math.round(estMB)} MB
              </div>
              {origShort > 0 && opts.shortSide > origShort && <div className="hint">★ El vídeo original es {origShort}p: exportar a más resolución no añade detalle.</div>}
              {opts.shortSide >= 2160 && <div className="hint">4K tarda bastante más en exportar.</div>}
            </div>
            {limitMsg && (
              <p className="plan-limit" role="alert">
                {limitMsg.text.replace(/\s*https?:\/\/\S+$/, '')}{' '}
                {(limitMsg.status === 402 || maxShort < 2160) && (
                  <a href={plansUrl(limitMsg.status === 402 ? 'editor-4k' : 'editor-export-limit')} target="_blank" rel="noopener">Ver planes</a>
                )}
              </p>
            )}
            {err && <p style={{ color: 'var(--danger)', wordBreak: 'break-word' }}>No se pudo exportar: {err}</p>}
            <div className="actions">
              <button className="btn" onClick={close}>Cancelar</button>
              <button className="btn primary" onClick={start}><Download size={16} />{phase === 'error' ? 'Reintentar' : 'Exportar'}</button>
            </div>
          </>
        ) : phase === 'running' ? (
          <>
            <p>Renderizando tu vídeo… Puedes esperar aquí; no cierres esta pestaña.</p>
            <div className="bigbar"><div style={{ width: `${pct}%` }} /></div>
            <div className="hint" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <span>{pct}%{prog?.speed ? ` · ${prog.speed.toFixed(1)}x` : ''}</span>
              <span>{eta !== null ? `Quedan ~${formatShort(eta)}` : 'Preparando…'}</span>
            </div>
            <div className="actions"><button className="btn" onClick={cancel}>Cancelar exportación</button></div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <CheckCircle2 color="var(--ok)" size={28} />
              <div><b>¡Tu vídeo está listo!</b><div className="hint">{size.width}×{size.height} · {formatShort(project.durationSec)}</div></div>
            </div>
            <div className="actions">
              <button className="btn" onClick={close}>Seguir editando</button>
              {dl && <a className="btn primary" href={dl} download><Download size={16} />Descargar MP4</a>}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
